/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { reatomQuickBuyInput } from '@root/features/trade/quick-buy/atoms/reatom-quick-buy-input'

import type { TradeSettingsQuick } from '@root/entities/trade-settings/model'

type Shape = 'pill' | 'line'

/**
 * The amount a quick buy spends, shown and edited in place.
 *
 * The design puts this number in two places — the pill in every column head and
 * the footer line — and in both you click the number and type. So this is one
 * component with two shapes rather than two components: the behaviour is the
 * same, only the frame around it differs.
 *
 * The editing itself belongs to `reatomQuickBuyInput`, the same model the old
 * board's input uses. It validates against the network's ceiling and saves half
 * a second after the last keystroke, so there is no Save button to draw and no
 * second copy of those rules to keep in step.
 */
export const QuickBuyAmount: React.FC<{
  section: TradeSettingsQuick.QuickSwapSection
  shape: Shape
}> = memo(({ section, shape }) => {
  const model = useMemo(() => reatomQuickBuyInput({ section }), [section])

  const [raw] = useAtom(model.raw)
  const [isValid] = useAtom(model.isValid)
  const setRaw = useAction(model.setRaw)
  const reset = useAction(model.reset)

  const [editing, setEditing] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) {
      input.current?.select()
    }
  }, [editing])

  const shown = raw.length > 0 ? raw : '—'

  if (editing) {
    return (
      <span className={shape === 'pill' ? 'qb qbedit' : 'fl qbedit'}>
        {shape === 'line' && <span className="qblabel">Quick buy</span>}
        <input
          ref={input}
          className="mono qbinput"
          value={raw}
          aria-label="The amount a quick buy spends"
          inputMode="decimal"
          autoFocus
          style={{
            /* Grow with the number instead of jumping to a fixed box. */
            width: `${Math.max(2, raw.length + 1)}ch`,
            color: isValid ? 'var(--t1)' : 'var(--down)',
          }}
          onChange={(event) => setRaw(event.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setEditing(false)
            }
            if (event.key === 'Escape') {
              /* Put back what was saved, then leave. */
              reset()
              setEditing(false)
            }
          }}
        />
        <span className="qbunit">SOL</span>
      </span>
    )
  }

  return (
    <button
      className={shape === 'pill' ? 'qb' : 'fl qbline'}
      type="button"
      title="Click to change what a quick buy spends"
      onClick={() => setEditing(true)}
    >
      {shape === 'pill' ? (
        <svg
          className="is"
          viewBox="0 0 24 24"
          style={{ width: 10, height: 10 }}
        >
          <path d="M13.2 2 4 14h5.8l-1 8L18 10h-5.8l1.2-8Z" />
        </svg>
      ) : (
        <span className="qblabel">Quick buy</span>
      )}
      <span className="mono" style={{ color: 'var(--t1)' }}>
        {shown}
      </span>
      {shape === 'line' && <span className="qbunit">SOL</span>}
    </button>
  )
})
